/**
 * MoneyPlanPage - mp_01 (admin-only trial, Money plan theme only).
 * Shows the money plan from Diary inside Worthly. Both apps read and save the
 * same plan at money_diary.plan, so nothing is copied and nothing is lost if
 * the trial is removed.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MoneyPlan from './MoneyPlan';
import './moneyPlan.css';

export default function MoneyPlanPage({ userData, onSave, onBack, refresh, startPage = null /* plan_27 */ }) {
  // Worthly is light-only, so the plan's dark-mode colours stay off.
  useEffect(() => {
    const el = document.documentElement;
    const had = el.classList.contains('force-light');
    el.classList.add('force-light');
    return () => { if (!had) el.classList.remove('force-light'); };
  }, []);
  // Opens straight away with what Worthly already has, and fetches your latest
  // data quietly in the background. If that finds a newer plan (say you just
  // changed it in Diary) and you haven't changed anything here yet, it
  // switches to the newer one.
  const md = (userData && userData.money_diary) || {};
  const [freshPlan, setFreshPlan] = useState(null);
  const [ver, setVer] = useState(0);
  const edited = useRef(false);
  const startPlan = useRef(md.plan);
  useEffect(() => {
    if (!refresh) return undefined;
    let on = true;
    Promise.resolve(refresh()).then((fresh) => {
      const fp = fresh && fresh.money_diary && fresh.money_diary.plan;
      const sp = startPlan.current;
      if (on && !edited.current && fp && (!sp || String(fp.updatedAt || '') > String(sp.updatedAt || ''))) {
        setFreshPlan(fp);
        setVer((v) => v + 1);
      }
    }).catch(() => {});
    return () => { on = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = (plan) => { edited.current = true; onSave(plan); };
  // Drawn over the whole app, so Home stays underneath while you swipe back.
  return createPortal(
    <div className="md" data-testid="money-plan-page">
      <MoneyPlan
        key={ver}
        saved={freshPlan || md.plan}
        raw={md}
        data={userData}
        why={md.why}
        topValues={Array.isArray(md.topValues) ? md.topValues : []}
        onSave={save}
        onBack={onBack}
        startPage={startPage}
      />
    </div>,
    document.body
  );
}
